// frontend/src/utils/reference/entityArbitrator.js

import {
  cloneEntityCandidate,
  createEmptyEntityState,
  dedupeEntities,
  sameEntity,
  sortEntitiesByScore,
} from './entityStateHelpers';
import {
  boostWinningEntity,
  demotePreviousEntity,
  getTopScoringEntity,
  scoreEntityCandidates,
} from './entityScorer';
import { assignEntityRoles } from './entityRoleAssigner';

function selectPreviousEntity({
  winner = null,
  scoredCandidates = [],
  currentEntity = null,
} = {}) {
  if (currentEntity && !sameEntity(currentEntity, winner)) {
    return demotePreviousEntity(currentEntity);
  }

  const fallbackPrevious = scoredCandidates.find(
    (candidate) => !sameEntity(candidate, winner)
  );

  return fallbackPrevious ? demotePreviousEntity(fallbackPrevious) : null;
}

function applyExplicitCorrectionPriority({
  winner = null,
  scoredCandidates = [],
} = {}) {
  const correctedCandidate = scoredCandidates.find(
    (candidate) => candidate?.signals?.corrected === true
  );

  if (!correctedCandidate) {
    return winner;
  }

  return cloneEntityCandidate(correctedCandidate);
}

// 🔥 NEW: force binding when signal is strong and uncontested
function shouldForceBind({ candidates = [], top = null } = {}) {
  if (!top) return false;

  const hasPronoun = top?.signals?.pronounSupported === true;
  const hasExplicit = top?.signals?.explicitlyNamed === true;
  const hasCorrection = top?.signals?.corrected === true;

  const hasStrongSignal = hasPronoun || hasExplicit || hasCorrection;
  if (!hasStrongSignal) return false;

  const second = candidates[1];
  if (!second) return true;

  const scoreGap = (top.score || 0) - (second.score || 0);

  // decisive win threshold
  if (scoreGap >= 3) return true;

  return false;
}

function arbitrateEntityState({
  candidates = [],
  currentEntity = null,
  previousActiveEntity = null,
  namedKeys = [],
  historicalKeys = [],
} = {}) {
  const baseState = createEmptyEntityState();

  const uniqueCandidates = dedupeEntities(candidates);

  if (uniqueCandidates.length === 0) {
    return baseState;
  }

  const scoredCandidates = scoreEntityCandidates({
    candidates: uniqueCandidates,
    currentEntity,
    previousActiveEntity,
  });

  const sortedScored = sortEntitiesByScore(scoredCandidates);
  const topScoringWinner = getTopScoringEntity(sortedScored);

  const selectedWinner = applyExplicitCorrectionPriority({
    winner: topScoringWinner,
    scoredCandidates: sortedScored,
  });

  // 🔥 NEW: FORCE BIND HERE
  if (shouldForceBind({ candidates: sortedScored, top: selectedWinner })) {
    const forcedWinner = boostWinningEntity(selectedWinner);

    const previousEntity = selectPreviousEntity({
      winner: forcedWinner,
      scoredCandidates: sortedScored,
      currentEntity,
    });

    const rescoredCandidates = sortEntitiesByScore(
      dedupeEntities([
        ...sortedScored,
        ...(forcedWinner ? [forcedWinner] : []),
        ...(previousEntity ? [previousEntity] : []),
      ])
    );

    const roleState = assignEntityRoles({
      candidates: rescoredCandidates,
      active: forcedWinner,
      previous: previousEntity,
      namedKeys,
      historicalKeys,
    });

    return {
      ...baseState,
      ...roleState,
      candidates: rescoredCandidates,
      mode: 'force_bind',
      preserveContinuity: true,
      pronounResolved:
        forcedWinner?.signals?.pronounSupported === true,
    };
  }

  // fallback to normal arbitration
  const boostedWinner = boostWinningEntity(selectedWinner);

  const previousEntity = selectPreviousEntity({
    winner: boostedWinner,
    scoredCandidates: sortedScored,
    currentEntity,
  });

  const rescoredCandidates = sortEntitiesByScore(
    dedupeEntities([
      ...sortedScored,
      ...(boostedWinner ? [boostedWinner] : []),
      ...(previousEntity ? [previousEntity] : []),
    ])
  );

  const roleState = assignEntityRoles({
    candidates: rescoredCandidates,
    active: boostedWinner,
    previous: previousEntity,
    namedKeys,
    historicalKeys,
  });

  return {
    ...baseState,
    ...roleState,
    candidates: rescoredCandidates,
  };
}

export { arbitrateEntityState };